import React, { useState } from 'react';
import { FaSearch, FaCheck, FaTimes, FaEye, FaDownload, FaFilter, FaWallet, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import './Withdrawals.css';

const Withdrawals = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedWithdrawals, setSelectedWithdrawals] = useState([]);

  // Dummy withdrawal data
  const [withdrawals] = useState([
    {
      id: 1,
      user: 'John Doe',
      email: 'john@example.com',
      walletAddress: '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb7',
      amount: '$1,200',
      currency: 'USDT',
      fee: '$12',
      netAmount: '$1,188',
      status: 'Pending',
      requestDate: '2024-01-15 10:30',
      network: 'Ethereum',
      priority: 'high'
    },
    {
      id: 2,
      user: 'Jane Smith',
      email: 'jane@example.com',
      walletAddress: '0x5aAeb6053f3E94C9b9A09f33669435E7Ef1BeAed',
      amount: '$3,500',
      currency: 'BTC',
      fee: '$35',
      netAmount: '$3,465',
      status: 'Processing',
      requestDate: '2024-01-15 09:15',
      network: 'Bitcoin',
      priority: 'medium'
    },
    {
      id: 3,
      user: 'Mike Johnson',
      email: 'mike@example.com',
      walletAddress: '0xfB6916095ca1df60bB79Ce92cE3Ea74c37c5d359',
      amount: '$750',
      currency: 'ETH',
      fee: '$7.50',
      netAmount: '$742.50',
      status: 'Approved',
      requestDate: '2024-01-14 16:45',
      network: 'Ethereum',
      priority: 'low'
    },
    {
      id: 4,
      user: 'Sarah Williams',
      email: 'sarah@example.com',
      walletAddress: '0xdbF03B407c01E7cD3CBea99509d93f8DDDC8C6FB',
      amount: '$2,000',
      currency: 'USDT',
      fee: '$20',
      netAmount: '$1,980',
      status: 'Pending',
      requestDate: '2024-01-14 14:30',
      network: 'BSC',
      priority: 'high'
    },
    {
      id: 5,
      user: 'Robert Brown',
      email: 'robert@example.com',
      walletAddress: '0xD1220A0cf47c7B9Be7A2E6BA89F429762e7b9aDb',
      amount: '$5,000',
      currency: 'BNB',
      fee: '$50',
      netAmount: '$4,950',
      status: 'Rejected',
      requestDate: '2024-01-13 11:00',
      network: 'BSC',
      priority: 'medium',
      rejectReason: 'Insufficient balance'
    }
  ]);

  // Filter withdrawals
  const filteredWithdrawals = withdrawals.filter(withdrawal => {
    const matchesSearch = withdrawal.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          withdrawal.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          withdrawal.walletAddress.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || withdrawal.status.toLowerCase() === filterStatus.toLowerCase();
    return matchesSearch && matchesFilter;
  });

  const handleApprove = (withdrawalId) => {
    console.log('Approving withdrawal:', withdrawalId);
    // Implement approve logic
  };

  const handleReject = (withdrawalId) => {
    console.log('Rejecting withdrawal:', withdrawalId);
    // Implement reject logic
  };

  const handleBatchApprove = () => {
    console.log('Batch approving:', selectedWithdrawals);
    // Implement batch approve logic
  };

  const toggleSelection = (withdrawalId) => {
    setSelectedWithdrawals(prev =>
      prev.includes(withdrawalId)
        ? prev.filter(id => id !== withdrawalId)
        : [...prev, withdrawalId]
    );
  };

  const getStatusColor = (status) => {
    switch(status.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'processing':
        return 'info';
      case 'rejected':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high':
        return 'danger';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="withdrawals-page">
      <div className="page-header">
        <h1>Withdrawal Management</h1>
        <p>Process and manage withdrawal requests</p>
      </div>

      {/* Statistics Cards */}
      <div className="withdrawals-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <FaWallet />
          </div>
          <div className="stat-info">
            <h4>Total Withdrawals</h4>
            <p>$189,240</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon pending">
            <FaClock />
          </div>
          <div className="stat-info">
            <h4>Pending Requests</h4>
            <p>18 Requests</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon warning">
            <FaExclamationTriangle />
          </div>
          <div className="stat-info">
            <h4>High Priority</h4>
            <p>5 Requests</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon success">
            <FaCheck />
          </div>
          <div className="stat-info">
            <h4>Processed Today</h4>
            <p>12 Withdrawals</p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="withdrawals-controls">
        <div className="search-wrapper">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by user, email, or wallet address..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-wrapper">
          <FaFilter className="filter-icon" />
          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {selectedWithdrawals.length > 0 && (
          <button className="btn btn-batch-approve" onClick={handleBatchApprove}>
            <FaCheck /> Batch Approve ({selectedWithdrawals.length})
          </button>
        )}

        <button className="btn btn-export">
          <FaDownload /> Export Data
        </button>
      </div>

      {/* Withdrawals Table */}
      <div className="withdrawals-table-container">
        <div className="table-responsive">
          <table className="withdrawals-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedWithdrawals(filteredWithdrawals.filter(w => w.status === 'Pending').map(w => w.id));
                      } else {
                        setSelectedWithdrawals([]);
                      }
                    }}
                  />
                </th>
                <th>ID</th>
                <th>User</th>
                <th>Wallet Address</th>
                <th>Amount</th>
                <th>Fee</th>
                <th>Net Amount</th>
                <th>Network</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Request Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredWithdrawals.map(withdrawal => (
                <tr key={withdrawal.id}>
                  <td>
                    {withdrawal.status === 'Pending' && (
                      <input
                        type="checkbox"
                        checked={selectedWithdrawals.includes(withdrawal.id)}
                        onChange={() => toggleSelection(withdrawal.id)}
                      />
                    )}
                  </td>
                  <td>#{withdrawal.id}</td>
                  <td>
                    <div className="user-info">
                      <span className="user-name">{withdrawal.user}</span>
                      <small className="user-email">{withdrawal.email}</small>
                    </div>
                  </td>
                  <td>
                    <div className="wallet-address">
                      {withdrawal.walletAddress.slice(0, 6)}...{withdrawal.walletAddress.slice(-4)}
                    </div>
                  </td>
                  <td className="amount">{withdrawal.amount}</td>
                  <td className="fee">{withdrawal.fee}</td>
                  <td className="net-amount">{withdrawal.netAmount}</td>
                  <td>{withdrawal.network}</td>
                  <td>
                    <span className={`priority-badge priority-${getPriorityColor(withdrawal.priority)}`}>
                      {withdrawal.priority}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge status-${getStatusColor(withdrawal.status)}`}>
                      {withdrawal.status}
                    </span>
                    {withdrawal.rejectReason && (
                      <small className="reject-reason">{withdrawal.rejectReason}</small>
                    )}
                  </td>
                  <td>{withdrawal.requestDate}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn view"
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                      {(withdrawal.status === 'Pending' || withdrawal.status === 'Processing') && (
                        <>
                          <button
                            className="action-btn approve"
                            onClick={() => handleApprove(withdrawal.id)}
                            title="Approve"
                          >
                            <FaCheck />
                          </button>
                          <button
                            className="action-btn reject"
                            onClick={() => handleReject(withdrawal.id)}
                            title="Reject"
                          >
                            <FaTimes />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Withdrawals;